import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import leapLogoWhite from '@/assets/leap-logo-white.png'
import { cn } from '@/utils/cn'

export type MobileAuthFeature = {
  icon: LucideIcon
  title: string
  description: string
}

type Props = {
  productName: string
  headline: string
  headlineAccent?: string
  subtitle?: string
  features?: MobileAuthFeature[]
  trailing?: ReactNode
  className?: string
}

export function MobileAuthHero({
  productName,
  headline,
  headlineAccent,
  subtitle,
  features,
  trailing,
  className,
}: Props) {
  return (
    <div className={cn('text-white', className)}>
      <div className="mb-2 flex min-h-9 items-center justify-between gap-3">
        <img
          src={leapLogoWhite}
          alt="LEAP"
          className="h-9 w-auto object-contain"
          style={{ aspectRatio: '880 / 400' }}
          draggable={false}
        />
        {trailing ? <div className="shrink-0 self-center">{trailing}</div> : null}
      </div>

      <p className="mt-1.5 text-[10px] font-extrabold uppercase tracking-[3px] text-white/75">
        {productName}
      </p>

      {subtitle ? (
        <p className="mt-3 text-sm font-semibold leading-5 text-white/90">{subtitle}</p>
      ) : null}

      <h1 className="mt-3.5 text-[26px] font-black leading-8 tracking-[-0.5px] text-white">
        {headline}
        {headlineAccent ? (
          <>
            <br />
            <span className="text-white/65">{headlineAccent}</span>
          </>
        ) : null}
      </h1>

      {features && features.length > 0 ? (
        <div className="mt-4 space-y-2.5">
          {features.slice(0, 3).map((f) => (
            <div key={f.title} className="flex items-start gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/12 bg-white/15">
                <f.icon size={18} className="text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-extrabold text-white">{f.title}</div>
                <div className="mt-0.5 text-[11px] leading-4 text-white/75">{f.description}</div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
